/**
 * Build multipart FormData for POST /api/v1/directors/create
 */
import { normalizeTypeKey } from './mandatoryDocumentTypes';

/** Normalize user/API date input to YYYY-MM-DD for APIs and Oracle TO_DATE. */
function normalizeToIsoDate(value) {
  if (value == null || value === '') return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const str = String(value).trim();
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const dmy = str.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  return '';
}

function formatDate(value) {
  const iso = normalizeToIsoDate(value);
  return iso || '';
}

export function buildDirectorFormData(formData) {
  const fd = new FormData();
  fd.append('company_id', String(formData.companyId));
  fd.append('first_name', formData.firstName || '');
  fd.append('last_name', formData.lastName || '');
  fd.append('email', formData.email || '');
  fd.append('phone', formData.mobileNumber || '');
  fd.append('phone_country_code', '+971');
  fd.append('emirates_id', formData.emiratesId || '');
  fd.append('passport_number', formData.passportNumber || '');
  fd.append('nationality', formData.nationality || '');
  fd.append('date_of_birth', formatDate(formData.dateOfBirth));
  fd.append('position', formData.position || 'Director');
  fd.append('appointment_date', formatDate(formData.appointmentDate));
  fd.append('is_active', formData.isActive !== false ? '1' : '0');
  fd.append('director_category', 'NON_INDEPENDENT');

  if (formData.address) {
    fd.append('official_street_address', formData.address);
    fd.append('official_country', 'UAE');
  }

  (formData.documents || []).forEach((doc) => {
    if (!doc.file) return;
    const typeCode = normalizeTypeKey(doc.typeCode || doc.type);
    if (typeCode === 'passport') {
      fd.append('passport_file', doc.file);
      if (doc.issueDate) fd.append('passport_issue_date', formatDate(doc.issueDate));
      if (doc.expiryDate) fd.append('passport_expiry_date', formatDate(doc.expiryDate));
    } else if (typeCode === 'emirates_id') {
      fd.append('emirates_id_file', doc.file);
      if (doc.issueDate) fd.append('emirates_id_issue_date', formatDate(doc.issueDate));
      if (doc.expiryDate) fd.append('emirates_id_expiry_date', formatDate(doc.expiryDate));
    } else if (typeCode === 'photo') {
      fd.append('photo', doc.file);
    } else {
      fd.append('documents', doc.file);
      if (typeCode) fd.append('doc_category', typeCode);
    }
  });

  return fd;
}

export function formatAppointmentDate(value) {
  return formatDate(value);
}

export { normalizeToIsoDate };

/** JSON payload for PUT /csadmin/directors/:id */
export function buildDirectorUpdatePayload(formData) {
  const payload = {
    first_name: formData.firstName || '',
    last_name: formData.lastName || '',
    emirates_id: formData.emiratesId || '',
    passport_number: formData.passportNumber || '',
    nationality: formData.nationality || '',
    email: formData.email || '',
    phone: formData.mobileNumber || '',
    official_street_address: formData.address || '',
    position: formData.position || '',
  };

  const dateOfBirth = formatDate(formData.dateOfBirth);
  const appointmentDate = formatDate(formData.appointmentDate);
  const passportExpiry = formatDate(formData.passportExpiryDate);
  const emiratesExpiry = formatDate(formData.emiratesIdExpiryDate);

  if (dateOfBirth) payload.date_of_birth = dateOfBirth;
  if (appointmentDate) payload.appointment_date = appointmentDate;
  if (passportExpiry) payload.passport_expiry_date = passportExpiry;
  if (emiratesExpiry) payload.emirates_id_expiry_date = emiratesExpiry;

  return payload;
}
